import { useEffect, useState } from 'react'
import { Typography } from 'antd'
import dayjs from 'dayjs'
import { BuildingFile } from 'types/building'

import Modal from 'components/atoms/Modal'
import Title from 'components/atoms/Title'

type Props = {
  file: BuildingFile
  onCancel: () => void
}

export default function ModalFileInfo({ file, onCancel }: Props) {
  const [dimensions, setDimensions] = useState<{
    width: number
    height: number
  } | null>(null)

  useEffect(() => {
    const ext =
      file.extension?.toLowerCase() || file.name.split('.').pop()?.toLowerCase()
    const typesImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']

    if (
      ext &&
      typesImage.includes(ext) &&
      !file.extraInfo?.width &&
      !file.extraInfo?.height
    ) {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      // Use s3Path if available, otherwise fallback to thumbnail
      img.src = file.s3Path || file.thumbnail
      img.onload = () => {
        setDimensions({ width: img.width, height: img.height })
      }
      img.onerror = () => {
        // If s3Path failed, try thumbnail if it's different
        if (file.s3Path && file.thumbnail && file.s3Path !== file.thumbnail) {
          const imgThumb = new Image()
          imgThumb.crossOrigin = 'anonymous'
          imgThumb.src = file.thumbnail
          imgThumb.onload = () => {
            setDimensions({ width: imgThumb.width, height: imgThumb.height })
          }
        }
      }
    }
  }, [file.s3Path, file.thumbnail, file.extraInfo, file.extension, file.name])

  const formatBytes = (
    bytes: number | string | undefined | null,
    decimals = 1
  ) => {
    if (!bytes) return '--'
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(Number(bytes)) / Math.log(k))
    return `${parseFloat((Number(bytes) / k ** i).toFixed(dm))}${sizes[i]}`
  }

  const formatDuration = (seconds: number | null | undefined) => {
    if (!seconds) return '--'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getFileSpecificInfo = () => {
    const ext =
      file.extension?.toLowerCase() || file.name.split('.').pop()?.toLowerCase()
    const { extraInfo } = file

    if (ext === 'pdf') {
      const pageCount = extraInfo?.page_count
      return {
        label: 'ページ数',
        value: pageCount ? `${pageCount}ページ` : '--'
      }
    }

    const typesVideo = ['mov', 'mp4']
    if (ext && typesVideo.includes(ext)) {
      return {
        label: '尺',
        value: formatDuration(extraInfo?.duration)
      }
    }

    const typesImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']
    if (ext && typesImage.includes(ext)) {
      const width = extraInfo?.width || dimensions?.width
      const height = extraInfo?.height || dimensions?.height
      return {
        label: 'サイズ',
        value: width && height ? `${width} x ${height}px` : '--'
      }
    }

    return null
  }

  const specificInfo = getFileSpecificInfo()
  const fileSize = file.extraInfo?.size || file.size

  return (
    <Modal width="550px" open onCancel={onCancel} closeIcon>
      <div className="flex flex-col items-center px-10 pb-5">
        <Title title="ファイル情報" />

        <div className="w-full flex flex-col gap-6 overflow-y-auto max-h-[550px] pr-2">
          <div className="flex flex-col gap-1">
            <Typography className="text-[#A0A0A0] font-bold text-base">
              名前
            </Typography>
            <Typography className="text-[#021120] text-lg">
              {file.name}
            </Typography>
          </div>

          <div className="flex flex-col gap-1">
            <Typography className="text-[#A0A0A0] font-bold text-base">
              最終更新者
            </Typography>
            <Typography className="text-[#021120] text-lg">
              {file.updatedUser
                ? `${file.updatedUser.lastName || ''} ${
                    file.updatedUser.firstName || ''
                  }`
                : '--'}
            </Typography>
          </div>

          {specificInfo && (
            <div className="flex flex-col gap-1">
              <Typography className="text-[#A0A0A0] font-bold text-base">
                {specificInfo.label}
              </Typography>
              <Typography className="text-[#021120] text-lg">
                {specificInfo.value}
              </Typography>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <Typography className="text-[#A0A0A0] font-bold text-base">
              容量
            </Typography>
            <Typography className="text-[#021120] text-lg">
              {formatBytes(fileSize)}
            </Typography>
          </div>

          <div className="flex flex-col gap-1">
            <Typography className="text-[#A0A0A0] font-bold text-base">
              作成日
            </Typography>
            <Typography className="text-[#021120] text-lg">
              {dayjs(file.createdAt).format('YYYY/MM/DD HH:mm')}
            </Typography>
          </div>

          <div className="flex flex-col gap-1">
            <Typography className="text-[#A0A0A0] font-bold text-base">
              更新日
            </Typography>
            <Typography className="text-[#021120] text-lg">
              {dayjs(file.updatedAt || file.createdAt).format(
                'YYYY/MM/DD HH:mm'
              )}
            </Typography>
          </div>
        </div>
      </div>
    </Modal>
  )
}
